import {
  CopyPlusIcon,
  FileClockIcon,
  FolderInputIcon,
  FolderSyncIcon,
  ReplaceIcon,
  SkipForwardIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';

import type { ConflictMode } from '../api';
import type { FolderConflictChoice } from '../lib/folderUpload';

import { useConflictStore, type ConflictRequest } from './conflictStore';
import { useFolderConflictStore, type FolderConflictRequest } from './folderConflictStore';

const SHOWN_NAMES = 5;

function Option({
  value,
  icon,
  title,
  hint,
}: {
  value: ConflictMode | FolderConflictChoice;
  icon: ReactNode;
  title: string;
  hint: string;
}) {
  const id = `disk-conflict-${value}`;
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-accent/50 has-[[data-state=checked]]:border-brand/60 has-[[data-state=checked]]:bg-brand-soft/40"
    >
      <RadioGroupItem id={id} value={value} className="mt-0.5" />
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground [&_svg]:size-4">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}

function ConflictForm({
  request,
  onSettle,
}: {
  request: ConflictRequest;
  onSettle: (mode: ConflictMode | null) => void;
}) {
  const { t } = useTranslation('disk');
  const [mode, setMode] = useState<ConflictMode>(request.allowVersion ? 'version' : 'rename');
  const extra = request.names.length - SHOWN_NAMES;
  const all = request.names.length === request.total;
  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('conflict.title', { count: request.names.length })}</DialogTitle>
        <DialogDescription>
          {t(`conflict.description.${request.operation}`, {
            folder: request.folderName,
            count: request.names.length,
          })}
        </DialogDescription>
      </DialogHeader>
      <ul className="space-y-1 rounded-lg bg-muted/50 px-3 py-2 text-sm" data-testid="disk-conflict-names">
        {request.names.slice(0, SHOWN_NAMES).map((name) => (
          <li key={name} className="truncate font-medium">
            {name}
          </li>
        ))}
        {extra > 0 && <li className="text-muted-foreground">{t('conflict.more', { count: extra })}</li>}
      </ul>
      <RadioGroup
        value={mode}
        onValueChange={(value) => setMode(value as ConflictMode)}
        aria-label={t('conflict.choose')}
      >
        {request.allowVersion && (
          <Option
            value="version"
            icon={<FileClockIcon />}
            title={t('conflict.version')}
            hint={t('conflict.versionHint')}
          />
        )}
        <Option
          value="rename"
          icon={<CopyPlusIcon />}
          title={t('conflict.rename')}
          hint={t('conflict.renameHint')}
        />
        <Option
          value="replace"
          icon={<ReplaceIcon />}
          title={t('conflict.replace')}
          hint={t('conflict.replaceHint')}
        />
        <Option
          value="skip"
          icon={<SkipForwardIcon />}
          title={t('conflict.skip')}
          hint={all ? t('conflict.skipAllHint') : t('conflict.skipHint')}
        />
      </RadioGroup>
      <DialogFooter>
        <Button variant="outline" onClick={() => onSettle(null)}>
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={() => onSettle(mode)}
          data-testid="disk-conflict-apply"
          variant={mode === 'replace' ? 'destructive' : 'default'}
        >
          {t('conflict.apply')}
        </Button>
      </DialogFooter>
    </>
  );
}

function FolderConflictForm({
  request,
  onSettle,
}: {
  request: FolderConflictRequest;
  onSettle: (choice: FolderConflictChoice | null) => void;
}) {
  const { t } = useTranslation('disk');
  const [choice, setChoice] = useState<FolderConflictChoice>(request.allowMerge ? 'mergeKeep' : 'keepBoth');
  const extra = request.names.length - SHOWN_NAMES;
  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('folderConflict.title', { count: request.names.length })}</DialogTitle>
        <DialogDescription>
          {t('folderConflict.description', { folder: request.folderName })}
        </DialogDescription>
      </DialogHeader>
      <ul className="space-y-1 rounded-lg bg-muted/50 px-3 py-2 text-sm" data-testid="disk-conflict-names">
        {request.names.slice(0, SHOWN_NAMES).map((name) => (
          <li key={name} className="truncate font-medium">
            {name}
          </li>
        ))}
        {extra > 0 && <li className="text-muted-foreground">{t('conflict.more', { count: extra })}</li>}
      </ul>
      <RadioGroup
        value={choice}
        onValueChange={(value) => setChoice(value as FolderConflictChoice)}
        aria-label={t('conflict.choose')}
      >
        {request.allowMerge && (
          <Option
            value="mergeKeep"
            icon={<FolderInputIcon />}
            title={t('folderConflict.mergeKeep')}
            hint={t('folderConflict.mergeKeepHint')}
          />
        )}
        {request.allowMerge && (
          <Option
            value="merge"
            icon={<FolderSyncIcon />}
            title={t('folderConflict.merge')}
            hint={t('folderConflict.mergeHint')}
          />
        )}
        <Option
          value="keepBoth"
          icon={<CopyPlusIcon />}
          title={t('folderConflict.keepBoth')}
          hint={t('folderConflict.keepBothHint')}
        />
        <Option
          value="skip"
          icon={<SkipForwardIcon />}
          title={t('folderConflict.skip')}
          hint={t('folderConflict.skipHint')}
        />
      </RadioGroup>
      <DialogFooter>
        <Button variant="outline" onClick={() => onSettle(null)}>
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={() => onSettle(choice)}
          data-testid="disk-conflict-apply"
          variant={choice === 'merge' ? 'destructive' : 'default'}
        >
          {t('conflict.apply')}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * The dialogs that resolve name conflicts of uploads, moves and copies (`askConflict`) and of the folders of a folder
 * upload (`askFolderConflict`).
 */
export function ConflictDialogHost() {
  const current = useConflictStore((s) => s.current);
  const settle = useConflictStore((s) => s.settle);
  const folderCurrent = useFolderConflictStore((s) => s.current);
  const folderSettle = useFolderConflictStore((s) => s.settle);
  return (
    <>
      <Dialog open={!!current} onOpenChange={(open) => !open && current && settle(current.id, null)}>
        <DialogContent size="md" data-testid="disk-conflict-dialog">
          {current && (
            <ConflictForm key={current.id} request={current} onSettle={(mode) => settle(current.id, mode)} />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!folderCurrent}
        onOpenChange={(open) => !open && folderCurrent && folderSettle(folderCurrent.id, null)}
      >
        <DialogContent size="md" data-testid="disk-folder-conflict-dialog">
          {folderCurrent && (
            <FolderConflictForm
              key={folderCurrent.id}
              request={folderCurrent}
              onSettle={(choice) => folderSettle(folderCurrent.id, choice)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
